// client/src/coms/views/canvas_parts/party/PartyReveals.js
// Reveal screens and galleries for every party mode. They read drawings from a drawing store, which is the
// live session's store during a game and a seeded store for saved results, so both look the same.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import UserAvatar from '../../games_parts/UserAvatar';
import PartyDrawingView from './PartyDrawingView';
import { BOARD, PALETTE, loadModelPictures, renderModel } from './partyDrawingModel';
import { encodeGif } from './gifEncoder';

export function nameOf(players, userId) {
  const player = (players || []).find((entry) => entry.userId === Number(userId));
  return player ? player.name : userId ? `Player ${userId}` : 'The deck';
}

function Credit({ players, userId, suffix = '' }) {
  if (!userId) return <span className="party-credit muted">The deck{suffix}</span>;
  return (
    <span className="party-credit">
      <UserAvatar userId={userId} name={nameOf(players, userId)} size={18} />
      {nameOf(players, userId)}{suffix}
    </span>
  );
}

function Reactions({ reactions = {}, mine = [], emojis = [], onReact, target, disabled }) {
  if (!emojis.length && !Object.keys(reactions).length) return null;
  const list = emojis.length ? emojis : Object.keys(reactions);
  return (
    <div className="party-reactions" role="group" aria-label="Reactions">
      {list.map((emoji) => (
        <button
          key={emoji}
          type="button"
          className={`party-reaction${mine.includes(emoji) ? ' mine' : ''}`}
          disabled={disabled || !onReact}
          aria-pressed={mine.includes(emoji)}
          onClick={() => onReact && onReact(target, emoji)}
        >
          {emoji}{reactions[emoji] ? <span>{reactions[emoji]}</span> : null}
        </button>
      ))}
    </div>
  );
}

// ---------------------------------------------------------------------------------------------- Sketch Relay
export function ChainEntries({ chain, store, players, emojis, onReact, latestIndex = -1 }) {
  return (
    <ol className="party-chain">
      {chain.entries.map((entry) => (
        <li key={entry.index} className={`party-chain-entry ${entry.kind}${entry.index === latestIndex ? ' fresh' : ''}`}>
          {entry.kind === 'drawing' ? (
            entry.missing ? <div className="party-missing">No drawing</div> : <PartyDrawingView store={store} drawingId={entry.drawingId} fluid label={`Drawing by ${nameOf(players, entry.userId)}`} />
          ) : (
            <div className={`party-chain-text${entry.missing ? ' missing' : ''}`}>
              {entry.kind === 'prompt' ? <span className="party-chain-tag">Prompt</span> : <span className="party-chain-tag">Guess</span>}
              “{entry.missing ? 'no answer' : entry.text}”
            </div>
          )}
          <div className="party-chain-meta">
            <Credit players={players} userId={entry.userId} />
            <Reactions reactions={entry.reactions} mine={entry.mine || []} emojis={emojis} onReact={onReact} target={entry.target} />
          </div>
        </li>
      ))}
    </ol>
  );
}

export function RelayReveal({ reveal, store, players, emojis, onReact }) {
  const chain = reveal.chains[reveal.chains.length - 1];
  const bottom = useRef(null);
  useEffect(() => {
    if (bottom.current && bottom.current.scrollIntoView) bottom.current.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [reveal.step, reveal.chain]);
  return (
    <div className="party-reveal" data-testid="relay-reveal">
      <div className="party-reveal-title">Chain {reveal.chain + 1}: started by <Credit players={players} userId={chain.ownerId} /></div>
      <ChainEntries chain={chain} store={store} players={players} emojis={emojis} onReact={onReact} latestIndex={reveal.step} />
      <div ref={bottom} />
    </div>
  );
}

// ---------------------------------------------------------------------------------------------- Flipbook
export function FlipbookPlayer({ book, store, players, defaultFps = 6, emojis, onReact, autoPlay = true }) {
  const frames = useMemo(() => book.frames.filter((frame) => frame.drawingId), [book.frames]);
  const [fps, setFps] = useState(defaultFps);
  const [playing, setPlaying] = useState(autoPlay);
  const [loop, setLoop] = useState(true);
  const [index, setIndex] = useState(0);
  const [exporting, setExporting] = useState('');
  const [, setLoaded] = useState(0);
  const canvasRef = useRef(null);

  useEffect(() => {
    frames.forEach((frame) => store.request(frame.drawingId));
    const unsubscribers = frames.map((frame) => store.subscribe(frame.drawingId, () => setLoaded((n) => n + 1)));
    return () => unsubscribers.forEach((fn) => fn());
  }, [frames, store]);

  useEffect(() => {
    if (!playing || frames.length < 2) return undefined;
    const timer = window.setInterval(() => {
      setIndex((current) => {
        if (current + 1 < frames.length) return current + 1;
        if (loop) return 0;
        setPlaying(false);
        return current;
      });
    }, 1000 / fps);
    return () => window.clearInterval(timer);
  }, [fps, frames.length, loop, playing]);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const width = 480;
    canvas.width = width;
    canvas.height = (width * BOARD.h) / BOARD.w;
    const frame = frames[index];
    const model = frame ? store.get(frame.drawingId) : null;
    renderModel(canvas.getContext('2d'), model, { onImage: () => setLoaded((n) => n + 1) });
  });

  const renderFrameTo = useCallback((ctx, frame) => {
    renderModel(ctx, store.get(frame.drawingId));
  }, [store]);
  const loadFrames = useCallback(() => loadModelPictures(frames.map((frame) => store.get(frame.drawingId))), [frames, store]);

  const download = (blob, extension) => {
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${(book.title || 'flipbook').replace(/[^a-z0-9]+/gi, '-').toLowerCase() || 'flipbook'}.${extension}`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    setTimeout(() => URL.revokeObjectURL(url), 5000);
  };

  const exportGif = () => {
    setExporting('gif');
    loadFrames().then(() => {
      try {
        const width = 320;
        const height = (width * BOARD.h) / BOARD.w;
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d', { willReadFrequently: true });
        const images = frames.map((frame) => {
          renderFrameTo(ctx, frame);
          return ctx.getImageData(0, 0, width, height);
        });
        download(encodeGif(images, { width, height, delayMs: 1000 / fps, palette: PALETTE }), 'gif');
      } finally {
        setExporting('');
      }
    });
  };

  const canRecord = typeof window !== 'undefined' && typeof window.MediaRecorder !== 'undefined' && typeof HTMLCanvasElement !== 'undefined' && HTMLCanvasElement.prototype.captureStream;
  const exportWebm = async () => {
    if (!canRecord) return;
    setExporting('webm');
    await loadFrames();
    const canvas = document.createElement('canvas');
    canvas.width = 640;
    canvas.height = 480;
    const ctx = canvas.getContext('2d');
    renderFrameTo(ctx, frames[0]);
    const stream = canvas.captureStream(fps);
    const type = window.MediaRecorder.isTypeSupported && window.MediaRecorder.isTypeSupported('video/webm;codecs=vp9') ? 'video/webm;codecs=vp9' : 'video/webm';
    const recorder = new window.MediaRecorder(stream, { mimeType: type });
    const chunks = [];
    recorder.ondataavailable = (event) => event.data && event.data.size && chunks.push(event.data);
    recorder.onstop = () => {
      download(new Blob(chunks, { type: 'video/webm' }), 'webm');
      setExporting('');
    };
    recorder.start();
    let k = 0;
    // Two loops, so the clip is long enough to enjoy.
    const timer = window.setInterval(() => {
      k += 1;
      if (k >= frames.length * 2) {
        window.clearInterval(timer);
        window.setTimeout(() => recorder.stop(), 1000 / fps);
        return;
      }
      renderFrameTo(ctx, frames[k % frames.length]);
    }, 1000 / fps);
  };

  const contributors = Array.from(new Set(frames.map((frame) => frame.userId)));
  return (
    <div className="party-flipbook" data-testid="flipbook-player">
      <div className="party-flipbook-title">{book.title ? `“${book.title}”` : `Flipbook ${book.index + 1}`}</div>
      <div className="party-flipbook-screen">
        <canvas ref={canvasRef} role="img" aria-label={`Frame ${index + 1} of ${frames.length}`} />
        <div className="party-flipbook-counter">{frames.length ? index + 1 : 0}/{frames.length}</div>
      </div>
      <div className="party-flipbook-controls">
        <button type="button" className="party-btn small" onClick={() => setPlaying((value) => !value)} aria-label={playing ? 'Pause' : 'Play'}>{playing ? '⏸ Pause' : '▶ Play'}</button>
        <label className="party-inline">Speed
          <input type="range" min="2" max="16" value={fps} onChange={(event) => setFps(Number(event.target.value))} aria-label="Frames per second" />
          <span>{fps} fps</span>
        </label>
        <label className="party-inline"><input type="checkbox" checked={loop} onChange={(event) => setLoop(event.target.checked)} /> Loop</label>
        <input type="range" min="0" max={Math.max(0, frames.length - 1)} value={index} onChange={(event) => { setPlaying(false); setIndex(Number(event.target.value)); }} aria-label="Frame" className="party-scrub" />
        <button type="button" className="party-btn small ghost" onClick={exportGif} disabled={!frames.length || Boolean(exporting)}>{exporting === 'gif' ? 'Encoding…' : 'Save GIF'}</button>
        {canRecord ? <button type="button" className="party-btn small ghost" onClick={exportWebm} disabled={!frames.length || Boolean(exporting)}>{exporting === 'webm' ? 'Recording…' : 'Save WebM'}</button> : null}
      </div>
      <div className="party-timeline" aria-label="Frames">
        {book.frames.map((frame, k) => (
          <button key={k} type="button" className={`party-timeline-frame${frames[index] && frames[index].drawingId === frame.drawingId ? ' active' : ''}`} onClick={() => { const at = frames.findIndex((f) => f.drawingId === frame.drawingId); if (at >= 0) { setPlaying(false); setIndex(at); } }} disabled={!frame.drawingId} aria-label={`Frame ${k + 1}`}>
            {frame.drawingId ? <PartyDrawingView store={store} drawingId={frame.drawingId} width={64} /> : <span className="party-missing tiny">–</span>}
          </button>
        ))}
      </div>
      <div className="party-chain-meta">
        <span className="party-credits">{contributors.map((userId) => <Credit key={userId} players={players} userId={userId} />)}</span>
        <Reactions reactions={book.reactions} mine={book.mine || []} emojis={emojis} onReact={onReact} target={`b${book.index}`} />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------------------------- vote galleries
export function RoundResults({ results, store, players, showSimilarity = false }) {
  if (!results) return null;
  return (
    <div className="party-round-results" data-testid="round-results">
      {results.prompt ? <div className="party-reveal-title">Prompt: “{results.prompt}”</div> : null}
      <div className="party-gallery-grid">
        {results.reference ? (
          <figure className="party-gallery-card reference">
            <PartyDrawingView items={results.reference.items} fluid label="Reference picture" />
            <figcaption><strong>Original</strong><span>{results.reference.description}</span></figcaption>
          </figure>
        ) : null}
        {results.results.map((entry, k) => (
          <figure key={entry.id} className={`party-gallery-card${k === 0 && entry.points > 0 ? ' best' : ''}`} style={{ animationDelay: `${k * 90}ms` }}>
            {entry.empty ? <div className="party-missing">No drawing</div> : <PartyDrawingView store={store} drawingId={entry.drawingId} fluid label={`Drawing by ${nameOf(players, entry.userId)}`} />}
            <figcaption>
              <Credit players={players} userId={entry.userId} />
              <span className="party-points">+{entry.points}</span>
              <span className="party-votes">{entry.votes} vote{entry.votes === 1 ? '' : 's'}</span>
              {showSimilarity && entry.similarity ? (
                <span className="party-similarity" title="Similarity estimate: overlap of the inked areas (one cell tolerance) and matching colours">
                  ≈{entry.similarity.score}% match (shape {entry.similarity.shape}%, colour {entry.similarity.color}%)
                </span>
              ) : null}
            </figcaption>
          </figure>
        ))}
      </div>
    </div>
  );
}

export function VoteGallery({ vote, store, onVote, disabled, layersFor, labelFor }) {
  if (!vote) return null;
  return (
    <div className="party-gallery-grid vote" data-testid="vote-gallery">
      {vote.entries.map((entry, k) => {
        const chosen = vote.myVote === entry.id;
        return (
          <figure key={entry.id} className={`party-gallery-card${chosen ? ' chosen' : ''}${entry.own ? ' own' : ''}`} style={{ animationDelay: `${k * 70}ms` }}>
            {layersFor ? <PartyDrawingView store={store} layers={layersFor(entry)} fluid /> : entry.drawingId ? <PartyDrawingView store={store} drawingId={entry.drawingId} fluid /> : null}
            {entry.text || (labelFor && labelFor(entry)) ? <div className="party-vote-text">{entry.text || labelFor(entry)}</div> : null}
            <figcaption>
              {entry.own ? <span className="party-own">Yours</span> : (
                <button type="button" className={`party-btn small${chosen ? '' : ' ghost'}`} disabled={disabled} onClick={() => onVote(entry.id)} data-testid={`vote-${entry.id}`}>
                  {chosen ? 'Voted ✓' : 'Vote'}
                </button>
              )}
            </figcaption>
          </figure>
        );
      })}
    </div>
  );
}

// ---------------------------------------------------------------------------------------------- Bluff & Brush
export function BluffReveal({ reveal, store, players }) {
  if (!reveal) return null;
  return (
    <div className="party-bluff-reveal" data-testid="bluff-reveal">
      <div className="party-bluff-art">
        <PartyDrawingView store={store} drawingId={reveal.drawingId} fluid label="The drawing" />
        <Credit players={players} userId={reveal.artistId} suffix=" drew" />
      </div>
      <ul className="party-bluff-options">
        {reveal.options.map((option, k) => (
          <li key={option.id} className={`party-bluff-option ${option.kind}`} style={{ animationDelay: `${k * 260}ms` }}>
            <div className="party-bluff-text">“{option.text}”</div>
            <div className="party-bluff-who">
              {option.kind === 'real' ? <strong className="party-truth">THE TRUTH</strong> : option.kind === 'house' ? <span className="muted">House lie</span> : <span>Lie by {option.owners.map((id) => nameOf(players, id)).join(' & ')}</span>}
            </div>
            <div className="party-bluff-voters">
              {option.voters.length ? option.voters.map((id) => <UserAvatar key={id} userId={id} name={nameOf(players, id)} size={22} />) : <span className="muted">No votes</span>}
            </div>
          </li>
        ))}
      </ul>
      {reveal.awards && reveal.awards.length ? (
        <div className="party-awards">{reveal.awards.map((award) => <span key={award.userId} className="party-award">{nameOf(players, award.userId)} +{award.points}</span>)}</div>
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------------------------- guessing turns
export function TurnReveal({ reveal, store, players }) {
  if (!reveal) return null;
  return (
    <div className="party-turn-reveal" data-testid="turn-reveal">
      <div className="party-answer-banner">The word was <strong>{reveal.word}</strong></div>
      <div className="party-turn-body">
        <PartyDrawingView store={store} drawingId={reveal.drawingId} fluid label="The drawing" />
        <ol className="party-guess-timeline">
          {reveal.guessed.length ? reveal.guessed.map((entry) => (
            <li key={entry.userId}><Credit players={players} userId={entry.userId} /> <span className="party-time">{(entry.atMs / 1000).toFixed(1)}s</span> <span className="party-points">+{entry.points}</span></li>
          )) : <li className="muted">Nobody got it.</li>}
          {reveal.drawerPoints ? <li><Credit players={players} userId={reveal.drawerId} suffix=" (artist)" /> <span className="party-points">+{reveal.drawerPoints}</span></li> : null}
        </ol>
      </div>
      {reveal.restrictions && reveal.restrictions.length ? <div className="party-restriction-tags">{reveal.restrictions.map((entry) => <span key={entry.id} className="party-tag">{entry.label}</span>)}</div> : null}
    </div>
  );
}

export function TeamReveal({ reveal, teams, store, players }) {
  if (!reveal) return null;
  const byId = new Map((teams || []).map((team) => [team.id, team]));
  return (
    <div className="party-team-reveal" data-testid="team-reveal">
      <div className="party-answer-banner">The word was <strong>{reveal.word}</strong></div>
      <div className="party-gallery-grid">
        {reveal.teams.map((entry) => {
          const team = byId.get(entry.teamId) || { name: `Team ${entry.teamId}`, color: '#888' };
          return (
            <figure key={entry.teamId} className="party-gallery-card" style={{ borderColor: team.color }}>
              <PartyDrawingView store={store} drawingId={entry.drawingId} fluid />
              <figcaption>
                <strong style={{ color: team.color }}>{team.name}</strong>
                <Credit players={players} userId={entry.artistId} suffix=" drew" />
                {entry.solved ? <span className="party-points">+{entry.points}{entry.order === 0 ? ' ⚡ first' : ''}</span> : <span className="muted">Not solved</span>}
              </figcaption>
            </figure>
          );
        })}
      </div>
    </div>
  );
}

export function ComboResults({ results, store, players }) {
  if (!results) return null;
  return (
    <div className="party-gallery-grid" data-testid="combo-results">
      {results.results.map((entry, k) => (
        <figure key={entry.id} className={`party-gallery-card combo${k === 0 && entry.votes ? ' best' : ''}`} style={{ animationDelay: `${k * 120}ms` }}>
          <PartyDrawingView store={store} layers={entry.layers} fluid label={`Combined picture: ${entry.prompt}`} />
          <figcaption>
            <strong>“{entry.prompt}”</strong>
            <span className="party-credits">{entry.members.map((member) => <Credit key={member.userId} players={players} userId={member.userId} suffix={` · ${member.part}`} />)}</span>
            <span className="party-votes">{entry.votes} vote{entry.votes === 1 ? '' : 's'}</span>
          </figcaption>
        </figure>
      ))}
    </div>
  );
}

export function ChallengeResults({ results, store, players }) {
  if (!results) return null;
  return (
    <div className="party-round-results" data-testid="challenge-results">
      <div className="party-reveal-title">{results.name}: “{results.prompt}”{results.twist ? ` → “${results.twist}”` : ''}</div>
      <div className="party-gallery-grid">
        {results.entries.map((entry) => (
          <figure key={entry.id} className="party-gallery-card">
            <PartyDrawingView store={store} drawingId={entry.drawingId} fluid />
            <figcaption>
              {entry.userId ? <Credit players={players} userId={entry.userId} suffix={entry.starter ? ` finished ${nameOf(players, entry.starter)}'s start` : ''} /> : null}
              {entry.contributors ? <span className="party-credits">{entry.contributors.map((id) => <Credit key={id} players={players} userId={id} />)}</span> : null}
              {entry.drawerId ? <Credit players={players} userId={entry.drawerId} suffix=" drew" /> : null}
              {entry.votes !== undefined ? <span className="party-votes">{entry.votes} vote{entry.votes === 1 ? '' : 's'}</span> : null}
            </figcaption>
          </figure>
        ))}
      </div>
      {results.awards && results.awards.length ? <div className="party-awards">{results.awards.map((award, k) => <span key={k} className="party-award">{nameOf(players, award.userId)} +{award.points}</span>)}</div> : null}
    </div>
  );
}

// ---------------------------------------------------------------------------------------------- full galleries
export function GameGallery({ gallery, store, players, teams }) {
  const [open, setOpen] = useState(0);
  if (!gallery) return null;
  if (gallery.type === 'chains') {
    const chain = gallery.chains[Math.min(open, gallery.chains.length - 1)];
    return (
      <div className="party-gallery" data-testid="gallery-chains">
        <div className="party-tabs" role="tablist">
          {gallery.chains.map((entry, k) => <button key={k} type="button" role="tab" aria-selected={open === k} className={`party-tab${open === k ? ' active' : ''}`} onClick={() => setOpen(k)}>{nameOf(players, entry.ownerId)}</button>)}
        </div>
        {chain ? <ChainEntries chain={chain} store={store} players={players} emojis={[]} /> : null}
      </div>
    );
  }
  if (gallery.type === 'flipbooks') {
    return <div className="party-gallery stack" data-testid="gallery-flipbooks">{gallery.books.map((book) => <FlipbookPlayer key={book.index} book={book} store={store} players={players} defaultFps={gallery.fps || 6} autoPlay={false} />)}</div>;
  }
  if (gallery.type === 'bluffs') {
    return <div className="party-gallery stack" data-testid="gallery-bluffs">{gallery.cases.map((entry, k) => <BluffReveal key={k} reveal={entry} store={store} players={players} />)}</div>;
  }
  if (gallery.type === 'turns') {
    return <div className="party-gallery stack" data-testid="gallery-turns">{gallery.turns.map((turn) => <TurnReveal key={turn.turnId} reveal={turn} store={store} players={players} />)}</div>;
  }
  if (gallery.type === 'teamTurns') {
    return <div className="party-gallery stack" data-testid="gallery-teams">{gallery.turns.map((turn, k) => <TeamReveal key={k} reveal={turn} teams={gallery.teams || teams} store={store} players={players} />)}</div>;
  }
  if (gallery.type === 'rounds') {
    return <div className="party-gallery stack" data-testid="gallery-rounds">{gallery.rounds.map((round) => <RoundResults key={round.index} results={round} store={store} players={players} showSimilarity={Boolean(round.reference)} />)}</div>;
  }
  if (gallery.type === 'combos') {
    return <div className="party-gallery stack" data-testid="gallery-combos">{gallery.rounds.map((round) => <ComboResults key={round.index} results={round} store={store} players={players} />)}</div>;
  }
  if (gallery.type === 'challenges') {
    return <div className="party-gallery stack" data-testid="gallery-challenges">{gallery.rounds.map((round, k) => <ChallengeResults key={k} results={round} store={store} players={players} />)}</div>;
  }
  return null;
}

export { Credit, Reactions };
